import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Divider,
} from '@mui/material';
import {
    Tv as TvIcon,
    CalendarToday as CalendarIcon,
    ViewInAr as VRIcon,
} from '@mui/icons-material';

const MaisonLumniProject: React.FC = () => {
    return (
        <Box>
            {/* Header avec titre et image */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <img
                    src="/TV/MaisonLumni.png"
                    alt="Logo La Maison Lumni"
                    style={{
                        width: '64px',
                        height: '64px',
                        objectFit: 'contain',
                        filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
                    }}
                />
                <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    La Maison Lumni
                </Typography>
            </Box>

            {/* Statut et date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                        2020
                    </Typography>
                </Box>
            </Box>

            {/* Description */}
            <Typography
                variant="body1"
                sx={{ mb: 3, lineHeight: 1.6 }}
            >
                Développement d'un système pour tourner l'émission "La Maison Lumni"
                en réalité virtuelle. Création d'outils innovants permettant aux équipes techniques
                de s'adapter aux nouvelles technologies de production virtuelle tout en conservant
                leurs workflows habituels et leur expertise métier.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Innovations développées */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Innovations développées
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Système de production virtuelle temps réel
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Intégration console d'éclairage grandMA
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Gestion lumières virtuelles
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Outils de transition workflow
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Formation équipes techniques
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Interface VCCU adaptée
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Technologies (générales) */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Domaines techniques
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['Production virtuelle', 'Réalité virtuelle temps réel', 'VCCU', 'Éclairage virtuel', 'Intégration hardware', 'Formation technique'].map((tech) => (
                        <Chip
                            key={tech}
                            label={tech}
                            variant="filled"
                            size="small"
                            sx={{
                                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                                color: '#a78bfa',
                                border: '1px solid rgba(167, 139, 250, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Défis techniques */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Défis techniques relevés
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Révolution technologique :</strong> Implémenter une solution de production
                    virtuelle complète tout en préservant l'expertise et les habitudes des équipes
                    techniques existantes.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Intégration transparente :</strong> Développer des interfaces permettant
                    aux opérateurs d'utiliser leurs outils familiers (grandMA) pour contrôler un
                    environnement virtuel complexe.
                </Typography>
                <Typography variant="body2">
                    <strong>Performance temps réel :</strong> Garantir une synchronisation parfaite
                    entre environnements virtuels et éléments physiques.
                </Typography>
            </Box>
        </Box>
    );
};

export default MaisonLumniProject;
